import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { AlertTriangle, TrendingDown, FileText, MessageSquare, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import ReportModal from "@/components/ReportModal";

const REFRESH_MS = 30_000;

/** Visual palette per severity — the whole card border/badge shift with it
 *  so a wall of Critical rows reads at-a-glance without squinting at text. */
const SEVERITY_STYLE = {
    critical: { label: "CRITICAL", ring: "border-red-500", chip: "bg-red-600 text-white border-red-700", swatch: "bg-red-600" },
    high:     { label: "HIGH",     ring: "border-orange-400", chip: "bg-orange-500 text-white border-orange-600", swatch: "bg-orange-500" },
    normal:   { label: "NORMAL",   ring: "border-blue-300", chip: "bg-blue-500 text-white border-blue-600", swatch: "bg-blue-500" },
    low:      { label: "LOW",      ring: "border-zinc-300", chip: "bg-zinc-400 text-white border-zinc-500", swatch: "bg-zinc-400" },
};

const KIND_ICON = {
    ticket: MessageSquare,
    alarm: AlertTriangle,
    performance: TrendingDown,
};

const KIND_LABEL = {
    ticket: "CUSTOMER TICKET",
    alarm: "LIVE ALARM",
    performance: "PERFORMANCE DROP",
};

function _fmtDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" }) + " IST";
    } catch { return iso; }
}

export default function ServiceRequired() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reportFor, setReportFor] = useState(null);
    const [lastRefreshed, setLastRefreshed] = useState(null);

    const load = useCallback(() => api.get("/service-required")
        .then(r => { setItems(r.data); setLastRefreshed(new Date()); })
        .catch(() => { /* silent — poller retries */ })
        .finally(() => setLoading(false)),
    []);

    // Real-time sync: poll every 30s so tickets raised by customers from
    // the portal show up on this page without an admin refresh.
    useEffect(() => {
        load();
        const t = setInterval(load, REFRESH_MS);
        return () => clearInterval(t);
    }, [load]);

    // Group by severity so operators see one row of Critical, then High, then Normal, then Low.
    const buckets = { critical: [], high: [], normal: [], low: [] };
    for (const it of items) buckets[it.severity]?.push(it);
    const counts = {
        critical: buckets.critical.length,
        high: buckets.high.length,
        normal: buckets.normal.length,
        low: buckets.low.length,
    };

    return (
        <div className="space-y-6" data-testid="service-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Priority Queue</div>
                    <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-900 mt-1">Service Required</h1>
                    <p className="text-sm text-zinc-500 mt-1">
                        Every open source of trouble in one queue — customer tickets, live SolarOS alarms, and severe
                        performance drops. Sorted <span className="font-medium">Critical → High → Normal → Low</span>.
                    </p>
                </div>
                <div className="flex items-center gap-3 text-xs font-mono">
                    <span className="text-red-600" data-testid="count-critical">{counts.critical} CRITICAL</span>
                    <span className="text-orange-500" data-testid="count-high">{counts.high} HIGH</span>
                    <span className="text-blue-500" data-testid="count-normal">{counts.normal} NORMAL</span>
                    <span className="text-zinc-400" data-testid="count-low">{counts.low} LOW</span>
                    {lastRefreshed && (
                        <span className="text-zinc-400 ml-2 border-l border-zinc-200 pl-3">
                            Synced {lastRefreshed.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })}
                        </span>
                    )}
                </div>
            </div>

            {loading ? <div className="text-sm text-zinc-500">Loading…</div> : items.length === 0 ? (
                <div className="bg-white border border-zinc-200 rounded-sm p-10 text-center" data-testid="service-empty">
                    <div className="text-[#16A34A] text-sm font-medium">All plants nominal.</div>
                    <div className="text-xs text-zinc-500 mt-1">No open tickets, alarms, or performance drops detected.</div>
                </div>
            ) : (
                <div className="space-y-6" data-testid="service-list">
                    {["critical", "high", "normal", "low"].map((sev) => (
                        buckets[sev].length > 0 && (
                            <SeverityGroup key={sev} sev={sev} rows={buckets[sev]} onReport={setReportFor} />
                        )
                    ))}
                </div>
            )}

            {reportFor && <ReportModal stationId={reportFor.station_id} ownerEmail={reportFor.owner_email} plantName={reportFor.customer_name || reportFor.plant_name} onClose={() => setReportFor(null)} />}
        </div>
    );
}

function SeverityGroup({ sev, rows, onReport }) {
    const style = SEVERITY_STYLE[sev];
    return (
        <div className="space-y-3" data-testid={`severity-group-${sev}`}>
            <div className="flex items-center gap-3">
                <span className={`w-1.5 h-6 rounded-sm ${style.swatch}`} />
                <span className="text-xs font-semibold uppercase tracking-widest text-zinc-700">
                    {style.label}
                    <span className="ml-2 text-zinc-400 font-mono">·  {rows.length} item{rows.length === 1 ? "" : "s"}</span>
                </span>
            </div>
            <div className="space-y-3">
                {rows.map((it, i) => <ServiceRow key={`${it.station_id}-${it.kind}-${it.ticket_id || it.latest_alert?.alertId || i}`} it={it} i={i} onReport={onReport} />)}
            </div>
        </div>
    );
}

function ServiceRow({ it, i, onReport }) {
    const style = SEVERITY_STYLE[it.severity];
    const Icon = KIND_ICON[it.kind] || AlertTriangle;
    return (
        <div className={`bg-white border-l-4 ${style.ring} border-t border-r border-b border-zinc-200 rounded-sm p-5 hover:border-zinc-300 transition-colors duration-200`} data-testid={`service-row-${i}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest rounded-sm px-1.5 py-0.5 border ${style.chip}`}>
                            {style.label}
                        </span>
                        <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-zinc-500 border border-zinc-200 rounded-sm px-1.5 py-0.5">
                            <Icon className="w-3 h-3" /> {KIND_LABEL[it.kind]}
                        </span>
                        {it.ticket_number && (
                            <span className="text-[10px] font-mono text-zinc-500" data-testid="ticket-number-badge">#{it.ticket_number}</span>
                        )}
                    </div>
                    <Link to={`/plants/${it.station_id}`} className="text-lg font-semibold text-zinc-900 hover:text-[#0A0A0A] block">
                        {it.customer_name || it.plant_name}
                    </Link>
                    <div className="text-sm text-zinc-500 mt-0.5">
                        {it.location ? `${it.location} · ` : ""}{it.address}
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">
                        Contact: <span className="text-zinc-700">{it.owner_name}</span>
                        {it.owner_email && <> · <a href={`mailto:${it.owner_email}`} className="text-zinc-700 hover:text-[#0A0A0A]">{it.owner_email}</a></>}
                        {it.contact_phone && <> · <span className="text-zinc-700 font-mono">{it.contact_phone}</span></>}
                    </div>

                    {/* Kind-specific detail row */}
                    {it.kind === "ticket" && (
                        <div className="mt-3 pt-3 border-t border-zinc-100 space-y-1" data-testid="service-ticket-detail">
                            <div className="text-sm text-zinc-800 font-medium">{it.ticket_subject}</div>
                            {it.ticket_description && (
                                <div className="text-xs text-zinc-600 whitespace-pre-wrap line-clamp-3">{it.ticket_description}</div>
                            )}
                            <div className="text-[11px] text-zinc-400 font-mono">
                                Raised by {it.opened_by || "customer"} · {_fmtDate(it.opening_date)}
                            </div>
                        </div>
                    )}
                    {it.kind === "alarm" && it.latest_alert && (
                        <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center gap-3 flex-wrap">
                            <span className="text-xs font-mono text-red-600 border border-red-500/30 bg-red-500/10 rounded-sm px-1.5 py-0.5">{it.latest_alert.protocolName}</span>
                            <span className="text-sm text-zinc-700">{it.latest_alert.alertName}</span>
                            <span className="text-xs text-zinc-400 font-mono ml-auto">
                                {new Date(it.latest_alert.alertStartTime * 1000).toLocaleString()}
                            </span>
                        </div>
                    )}
                    {it.kind === "performance" && (
                        <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center gap-2 flex-wrap">
                            <span className="inline-flex items-center gap-1 text-xs font-mono text-[#F97316] border border-[#F97316]/30 bg-[#F97316]/10 rounded-full px-2.5 py-0.5">
                                <Zap className="w-3 h-3" /> {it.performance_drop_pct}% below expected
                            </span>
                        </div>
                    )}
                </div>

                <div className="flex flex-col gap-2 items-end">
                    <div className="text-right font-mono">
                        <div className="text-2xl font-medium text-zinc-900">{(it.current_power_kw ?? 0).toFixed(1)} <span className="text-xs text-zinc-500">kW</span></div>
                        <div className="text-[10px] text-zinc-500 uppercase tracking-wider">of {(it.expected_power_kw ?? 0).toFixed(1)} kW expected</div>
                    </div>
                    <Button size="sm" onClick={() => onReport(it)} className="bg-[#0A0A0A] hover:bg-black text-white rounded-full h-9 px-4 text-xs font-semibold" data-testid={`send-report-btn-${i}`}>
                        <FileText className="w-3 h-3 mr-1.5" /> Email Report
                    </Button>
                </div>
            </div>
        </div>
    );
}
